import React, { useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import Markdown from "markdown-to-jsx";
import {
  Fab,
  Paper,
  IconButton,
  TextField,
  Typography,
  Avatar,
  Chip,
  Zoom,
} from "@material-ui/core";
import ChatIcon from "@material-ui/icons/Chat";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import { makeStyles } from "@material-ui/core/styles";

import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";
import { streamOnboardingChat } from "../../services/onboardingAssistant";

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const assistantBubbleBg = isDark
    ? theme.palette.grey[800]
    : theme.palette.grey[100];
  const assistantBubbleText = theme.palette.text.primary;

  return {
    fab: {
      position: "fixed",
      // Fora do canto inferior: em varias telas ele cobria paginacao,
      // "carregar mais" e totais que vivem ali.
      bottom: 96,
      right: 24,
      zIndex: 1300,
    },
    panel: {
      position: "fixed",
      bottom: 160,
      right: 24,
      width: PANEL_WIDTH,
      height: PANEL_HEIGHT,
      maxHeight: "calc(var(--vh-full) - 184px)",
      display: "flex",
      flexDirection: "column",
      zIndex: 1300,
      overflow: "hidden",
      borderRadius: 16,
      [theme.breakpoints.down("xs")]: {
        width: "calc(100vw - 24px)",
        height: "calc(var(--vh-full) - 168px)",
        right: 12,
        bottom: 152,
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    headerAvatar: {
      width: 32,
      height: 32,
      background: "rgba(255,255,255,0.18)",
      color: theme.palette.primary.contrastText,
    },
    headerText: { flex: 1, minWidth: 0 },
    headerTitle: { fontWeight: 600, lineHeight: 1.2 },
    headerSubtitle: { opacity: 0.85, fontSize: "0.72rem" },
    headerClose: { color: theme.palette.primary.contrastText },
    messages: {
      flex: 1,
      overflowY: "auto",
      padding: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
      background: theme.palette.background.default,
    },
    row: { display: "flex", alignItems: "flex-end", gap: theme.spacing(1) },
    rowUser: { flexDirection: "row-reverse" },
    botAvatar: {
      width: 26,
      height: 26,
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      flexShrink: 0,
    },
    bubble: {
      maxWidth: "82%",
      padding: theme.spacing(1, 1.5),
      borderRadius: 14,
      fontSize: "0.875rem",
      lineHeight: 1.55,
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    },
    bubbleUser: {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderBottomRightRadius: 4,
    },
    bubbleBot: {
      background: assistantBubbleBg,
      color: assistantBubbleText,
      borderBottomLeftRadius: 4,
    },
    // Markdown element spacing/typography inside an assistant bubble
    md: {
      "& > *:first-child": { marginTop: 0 },
      "& > *:last-child": { marginBottom: 0 },
    },
    mdP: { margin: theme.spacing(0.75, 0) },
    mdH: {
      margin: theme.spacing(1.25, 0, 0.5),
      fontWeight: 600,
      fontSize: "0.95rem",
      lineHeight: 1.3,
    },
    mdList: {
      margin: theme.spacing(0.5, 0),
      paddingLeft: theme.spacing(2.5),
    },
    mdLi: { margin: theme.spacing(0.25, 0) },
    mdCode: {
      background: isDark ? theme.palette.grey[900] : "rgba(0,0,0,0.07)",
      borderRadius: 4,
      padding: "1px 5px",
      fontSize: "0.82em",
      fontFamily:
        "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
      wordBreak: "break-word",
    },
    mdLink: {
      color: isDark
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
      textDecoration: "underline",
    },
    typing: { display: "inline-flex", gap: 4, padding: theme.spacing(0.5, 0) },
    dot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: theme.palette.text.secondary,
      animation: "$blink 1.4s infinite both",
    },
    dot2: { animationDelay: "0.2s" },
    dot3: { animationDelay: "0.4s" },
    "@keyframes blink": {
      "0%, 80%, 100%": { opacity: 0.2 },
      "40%": { opacity: 1 },
    },
    empty: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      color: theme.palette.text.secondary,
      gap: theme.spacing(1.5),
      padding: theme.spacing(2),
    },
    emptyIcon: { fontSize: 40, opacity: 0.6 },
    suggestions: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      justifyContent: "center",
    },
    inputRow: {
      display: "flex",
      alignItems: "flex-end",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
      borderTop: `1px solid ${theme.palette.divider}`,
      background: theme.palette.background.paper,
    },
  };
});

const SUGGESTIONS = [
  "Como conecto meu WhatsApp via QR Code?",
  "Como configuro o WhatsApp Oficial (API)?",
  "Onde encontro o Token de envio?",
];

const AssistantMarkdown = ({ content }) => {
  const classes = useStyles();
  const options = React.useMemo(
    () => ({
      disableParsingRawHTML: true,
      overrides: {
        p: { props: { className: classes.mdP } },
        h1: { props: { className: classes.mdH } },
        h2: { props: { className: classes.mdH } },
        h3: { props: { className: classes.mdH } },
        h4: { props: { className: classes.mdH } },
        h5: { props: { className: classes.mdH } },
        h6: { props: { className: classes.mdH } },
        ul: { props: { className: classes.mdList } },
        ol: { props: { className: classes.mdList } },
        li: { props: { className: classes.mdLi } },
        code: { props: { className: classes.mdCode } },
        a: {
          props: {
            className: classes.mdLink,
            target: "_blank",
            rel: "noopener noreferrer",
          },
        },
      },
    }),
    [classes]
  );

  return (
    <div className={classes.md}>
      <Markdown options={options}>{content}</Markdown>
    </div>
  );
};

const TypingIndicator = () => {
  const classes = useStyles();
  return (
    <span className={classes.typing} aria-label="Assistente está digitando">
      <span className={classes.dot} />
      <span className={`${classes.dot} ${classes.dot2}`} />
      <span className={`${classes.dot} ${classes.dot3}`} />
    </span>
  );
};

const OnboardingAssistant = () => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { getAll } = useSettings();

  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  // Tela de Ajuda e menu do usuário abrem o assistente por evento global.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("pulse:open-onboarding", onOpen);
    return () => window.removeEventListener("pulse:open-onboarding", onOpen);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const settings = await getAll();
        const findValue = (k) => {
          const s = (settings || []).find((item) => item.key === k);
          return s ? s.value : undefined;
        };
        const isEnabled = findValue("onboardingAssistantEnabled") === "enabled";
        const allowed = (findValue("onboardingAssistantProfiles") || "admin")
          .split(",")
          .map((p) => p.trim());
        if (active) {
          setVisible(isEnabled && allowed.includes(user.profile));
        }
      } catch (e) {
        if (active) setVisible(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.profile]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!visible) return null;

  const sendMessage = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || streaming) return;
    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages([...nextMessages, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    await streamOnboardingChat({
      route: location.pathname,
      messages: nextMessages,
      onDelta: (delta) =>
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content: copy[copy.length - 1].content + delta,
          };
          return copy;
        }),
      onDone: () => setStreaming(false),
      onError: () => {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "Não consegui responder agora. Tente novamente em instantes.",
          };
          return copy;
        });
        setStreaming(false);
      },
    });
  };

  const handleSend = () => sendMessage(input);

  return (
    <>
      <Zoom in={open} unmountOnExit>
        <Paper className={classes.panel} elevation={8}>
          <header className={classes.header}>
            <Avatar className={classes.headerAvatar}>
              <EmojiObjectsOutlinedIcon fontSize="small" />
            </Avatar>
            <div className={classes.headerText}>
              <Typography variant="subtitle1" className={classes.headerTitle}>
                Assistente de onboarding
              </Typography>
              <Typography className={classes.headerSubtitle}>
                Tire dúvidas de configuração do sistema
              </Typography>
            </div>
            <IconButton
              size="small"
              className={classes.headerClose}
              aria-label="Fechar assistente"
              onClick={() => setOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </header>

          <div className={classes.messages} role="log" aria-live="polite">
            {messages.length === 0 ? (
              <div className={classes.empty}>
                <EmojiObjectsOutlinedIcon className={classes.emptyIcon} />
                <Typography variant="body2">
                  Olá! Posso te guiar na configuração do sistema. Pergunte algo
                  ou comece por uma sugestão:
                </Typography>
                <div className={classes.suggestions}>
                  {SUGGESTIONS.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() => sendMessage(s)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => {
                const isUser = m.role === "user";
                const isStreamingThis =
                  !isUser &&
                  streaming &&
                  i === messages.length - 1 &&
                  !m.content;
                return (
                  <div
                    key={i}
                    className={`${classes.row} ${isUser ? classes.rowUser : ""}`}
                  >
                    {!isUser && (
                      <Avatar className={classes.botAvatar}>
                        <EmojiObjectsOutlinedIcon style={{ fontSize: 16 }} />
                      </Avatar>
                    )}
                    <div
                      className={`${classes.bubble} ${
                        isUser ? classes.bubbleUser : classes.bubbleBot
                      }`}
                    >
                      {isUser ? (
                        m.content
                      ) : isStreamingThis ? (
                        <TypingIndicator />
                      ) : (
                        <AssistantMarkdown content={m.content} />
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          <div className={classes.inputRow}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              multiline
              rowsMax={4}
              placeholder="Digite sua dúvida..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              inputProps={{ "aria-label": "Mensagem para o assistente" }}
            />
            <IconButton
              color="primary"
              aria-label="Enviar mensagem"
              onClick={handleSend}
              disabled={streaming || !input.trim()}
            >
              <SendIcon />
            </IconButton>
          </div>
        </Paper>
      </Zoom>

      <Fab
        color="primary"
        className={classes.fab}
        aria-label={open ? "Fechar assistente de onboarding" : "Abrir assistente de onboarding"}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </Fab>
    </>
  );
};

export default OnboardingAssistant;
